import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import useFetch from 'use-http';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PayHistoryItem from 'src/models/personnel/PayHistoryItem';
import EmployeePayrollSearch, { IEmployeePayrollSearchCriteria } from '../components/EmployeePayrollSearch';
import BuildPayrollConfirmDialog from '../dialogs/employeePayroll/BuildPayrollConfirmDialog';
import TextExportDialog from '../dialogs/employeePayroll/TextExportDialog';
import ExcelReportDialog from '../dialogs/employeePayroll/ExcelReportDialog';
import CpiSpecialDialog from '../dialogs/employeePayroll/CpiSpecialDialog';
import PayHistoryCpiSpecialItem from 'src/models/personnel/PayHistoryCpiSpecialItem';
import LockPeriodDialog from '../dialogs/employeePayroll/LockPeriodDialog';
import TimeLockItem from 'src/models/personnel/TimeLockItem';
import TimeEmpCutoffDialog from '../dialogs/employeePayroll/TimeEmpCutoffDialog';
import TimeEmpCutoffItem from 'src/models/personnel/TimeEmpCutoffItem';
import PeriodCountDialog from '../dialogs/employeePayroll/PeriodCountDialog';
import PeriodCountItem from 'src/models/personnel/PeriodCountItem';
import HistPerfBonusDialog, { IHistPerfBonusReportParams } from '../dialogs/employeePayroll/HistPerfBonusDialog';
import NoSelectedInvoicesWarningDialog from 'src/components/accounts-payable/dialogs/NoSelectedInvoicesWarningDialog';
import DialogList from 'src/dialogs';
import actions from 'src/actions';

// Builds the payhistory GET's query string from every field the search panel exposes - blank/
// undefined fields are simply omitted (mirrors Oracle Forms' own Enter Query semantics: a field
// left blank isn't part of the query).
const buildPayHistoryQuery = (criteria: IEmployeePayrollSearchCriteria): string => {
    const query = new URLSearchParams({ periodNo: String(criteria.periodNo) });
    const addIfPresent = (key: string, value: string | number | undefined) => {
        if (value !== undefined && value !== null && value !== '') {
            query.set(key, String(value));
        }
    };
    addIfPresent('tsdNo', criteria.tsdNo);
    addIfPresent('name', criteria.name);
    addIfPresent('payxDept', criteria.payxDept);
    addIfPresent('payxNo', criteria.payxNo);
    addIfPresent('hours', criteria.hours);
    addIfPresent('otHours', criteria.otHours);
    addIfPresent('pcHours', criteria.pcHours);
    addIfPresent('persHours', criteria.persHours);
    addIfPresent('vacHours', criteria.vacHours);
    addIfPresent('holHours', criteria.holHours);
    addIfPresent('wSalary', criteria.wSalary);
    addIfPresent('prodBonus', criteria.prodBonus);
    addIfPresent('commission', criteria.commission);
    addIfPresent('areaCommission', criteria.areaCommission);
    addIfPresent('extCommission', criteria.extCommission);
    addIfPresent('extAreaCommission', criteria.extAreaCommission);
    addIfPresent('grossPay', criteria.grossPay);
    addIfPresent('expenses', criteria.expenses);
    addIfPresent('payType', criteria.payType);
    addIfPresent('lockRecord', criteria.lockRecord);
    return `?${query.toString()}`;
};

export const formatCurrency = (value: number) =>
    (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Rebuilds the grid's react-hook-form default values from a freshly fetched row set - passed to
// reset() after every fetch (initial search, and after Save Changes/Lock All/Unlock All re-fetch)
// so formState.isDirty accurately reflects "has anything changed since the data was last loaded",
// not stale edits from a previous query's rows.
const buildPayHistoryFormDefaults = (items: Array<PayHistoryItem>) => ({
    payHistoryItems: items.map((item) => ({
        hours: item.hours ?? '',
        otHours: item.otHours ?? '',
        pcHours: item.pcHours ?? '',
        persHours: item.persHours ?? '',
        vacHours: item.vacHours ?? '',
        holHours: item.holHours ?? '',
        grossPay: item.grossPay ?? '',
        lockRecord: item.lockRecord === 'Y',
    })),
});

// Mirrors CALC_PAY [oid 102] - only HOURLY pay types get an auto-recalculated Gross Pay as hours
// are edited (any other pay type's gross pay is set elsewhere, e.g. by Build Payroll, and isn't
// touched here just because an hours field changed).
export const calcGrossPay = (item: PayHistoryItem): number => {
    const rate = item.wSalary ?? 0;
    return (
        Math.round((item.hours ?? 0) * rate * 100) / 100 +
        Math.round((item.otHours ?? 0) * rate * 1.5 * 100) / 100 +
        Math.round((item.persHours ?? 0) * rate * 100) / 100 +
        Math.round((item.vacHours ?? 0) * rate * 100) / 100 +
        Math.round((item.holHours ?? 0) * rate * 100) / 100
    );
};

// AM/PM Excel navigates to a whole separate Oracle Forms block/screen (Excel_AMPM) that hasn't
// been screenshotted yet - rendered here to match the screen, but left as a stub until it has
// its own screenshot to port. (Excel Report, the other button that used to share this stub, is
// now wired up - see handleExcelReportClick.)
const NOT_YET_IMPLEMENTED_MESSAGE = 'This action has not been built yet.';

const EmployeePayrollPage = () => {
    const dispatch = useDispatch();
    const { get, post, put, response } = useFetch();
    const { register, reset, setValue, getValues, errors, formState } = useForm();
    const { isDirty } = formState;
    const [periodNo, setPeriodNo] = useState<number | undefined>(undefined);
    // Kept so Save Changes/Lock All/Unlock All re-fetch with the same filters the user searched
    // with, instead of dropping back to an unfiltered "whole period" view.
    const [lastSearchCriteria, setLastSearchCriteria] = useState<IEmployeePayrollSearchCriteria | undefined>(undefined);
    const [payHistoryItems, setPayHistoryItems] = useState<Array<PayHistoryItem>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    // usp_Build_Pay_History rebuilds the whole period's pay history and can take a while - this
    // disables the button and swaps its label so a slow run doesn't look like a dead click.
    const [buildingPayroll, setBuildingPayroll] = useState<boolean>(false);
    // Drives the Generate dialog's own message line - populated when Check_Staffing/
    // Duplicate_MultiBilling blocks the build, mirroring Oracle's own Alert_Message for each.
    const [buildPayrollMessage, setBuildPayrollMessage] = useState<string>('');
    // Text Export File's own dialog state.
    const [exportingText, setExportingText] = useState<boolean>(false);
    const [textExportMessage, setTextExportMessage] = useState<string>('');
    // Excel Report's own dialog state - same fetch-then-open shape as Text Export's.
    const [generatingExcelReport, setGeneratingExcelReport] = useState<boolean>(false);
    const [excelReportMessage, setExcelReportMessage] = useState<string>('');
    // CPI Special's own dialog state - fetched fresh each time the button is clicked (mirrors
    // showGenerate's pattern in CheckRegisterPage: fetch, then open, rather than relying on the
    // dialog to fetch its own data - Dialog's children unmount while closed, so a dialog-owned
    // fetch would need to re-run on every open anyway).
    const [cpiSpecialItems, setCpiSpecialItems] = useState<Array<PayHistoryCpiSpecialItem>>([]);
    const [savingCpiSpecial, setSavingCpiSpecial] = useState<boolean>(false);
    // Lock Period's own dialog state - fetched fresh each time the button is clicked, same pattern
    // as CPI Special (Dialog's children unmount while closed).
    const [lockPeriodItems, setLockPeriodItems] = useState<Array<TimeLockItem>>([]);
    // Set Cutoff EMail Date's own dialog state - same fetch-then-open pattern as CPI Special.
    const [timeEmpCutoffItems, setTimeEmpCutoffItems] = useState<Array<TimeEmpCutoffItem>>([]);
    const [savingTimeEmpCutoff, setSavingTimeEmpCutoff] = useState<boolean>(false);
    // Period Count's own dialog state - read-only report, same fetch-then-open pattern.
    const [periodCountItems, setPeriodCountItems] = useState<Array<PeriodCountItem>>([]);
    // Hist. Perf. Bonus's own dialog state - drives its MSG-equivalent status line, same idea as
    // Text Export/Build Payroll's own message state.
    const [histPerfBonusMessage, setHistPerfBonusMessage] = useState<string>('');

    const fetchPayHistoryItems = async (criteria: IEmployeePayrollSearchCriteria) => {
        setLoading(true);
        const result = await get(`/v1.0/Payroll/payhistory${buildPayHistoryQuery(criteria)}`);
        const items: Array<PayHistoryItem> = response.ok ? (result?.items ?? []) : [];
        setPayHistoryItems(items);
        // Re-syncs the grid's default values to what was just loaded and clears isDirty - without
        // this, edits from a previous query's rows (or a just-saved row) would keep the Save
        // Changes button enabled even though nothing on the newly loaded grid has changed yet.
        reset(buildPayHistoryFormDefaults(items));
        setLoading(false);
    };

    // Mirrors :control.period_no being set as soon as KEY-NEXT-ITEM validates the field - every
    // option in the Period No. Select already comes from a validated Pay_Periods lookup, so this
    // is available to Build Payroll/Lock All/Unlock All the moment a period is picked, independent
    // of whether Search has been clicked (Search only re-queries the grid itself).
    const handlePeriodSelected = (period: number | undefined) => {
        setPeriodNo(period);
    };

    const handleSearch = async (criteria: IEmployeePayrollSearchCriteria) => {
        setPeriodNo(criteria.periodNo);
        setLastSearchCriteria(criteria);
        await fetchPayHistoryItems(criteria);
    };

    // Mirrors CALC_PAY - recalculates Gross Pay from the row's current hours whenever an hours
    // field is edited, then re-executes Post-Query's own visual-attribute logic isn't reproduced
    // here (no red/green highlighting on this first pass).
    const handleHoursBlur = (index: number) => {
        const item = payHistoryItems[index];
        if (item.payType !== 'HOURLY') {
            return;
        }
        const values = getValues();
        const row = values?.payHistoryItems?.[index] ?? {};
        const recalculated: PayHistoryItem = {
            ...item,
            hours: Number(row.hours) || 0,
            otHours: Number(row.otHours) || 0,
            persHours: Number(row.persHours) || 0,
            vacHours: Number(row.vacHours) || 0,
            holHours: Number(row.holHours) || 0,
        };
        setValue(`payHistoryItems[${index}].grossPay`, calcGrossPay(recalculated).toFixed(2), { shouldDirty: true });
    };

    // Save Changes button - commits every row's editable fields (mirrors the ON-UPDATE trigger's
    // own column set: Reg/OT/Pers/Vac/Hol/PC Hours, Gross Pay, and Lock?).
    const handleSaveChanges = async () => {
        if (periodNo === undefined) {
            return;
        }
        const values = getValues();
        const updated: Array<PayHistoryItem> = payHistoryItems.map((item, index) => {
            const row = values?.payHistoryItems?.[index] ?? {};
            return {
                ...item,
                hours: Number(row.hours) || 0,
                otHours: Number(row.otHours) || 0,
                pcHours: Number(row.pcHours) || 0,
                persHours: Number(row.persHours) || 0,
                vacHours: Number(row.vacHours) || 0,
                holHours: Number(row.holHours) || 0,
                grossPay: Number(row.grossPay) || 0,
                lockRecord: row.lockRecord ? 'Y' : 'N',
            };
        });

        await put('/v1.0/Payroll/payhistory', updated);
        if (response.ok) {
            await fetchPayHistoryItems(lastSearchCriteria ?? { periodNo });
        } else {
            alert('Unable to save changes. Please try again.');
        }
    };

    const showDialog = (dialog: DialogList) => dispatch(actions.layout.showDialog(dialog));
    const hideDialog = () => dispatch(actions.layout.hideDialog());

    // Lock All / Unlock All buttons.
    const handleLockAll = async (locked: boolean) => {
        if (periodNo === undefined) {
            showDialog(DialogList.EMPLOYEE_PAYROLL_NO_PERIOD);
            return;
        }
        await put('/v1.0/Payroll/payhistory/lockall', { periodNo, locked });
        if (response.ok) {
            await fetchPayHistoryItems(lastSearchCriteria ?? { periodNo });
        } else {
            alert('Unable to update locked status. Please try again.');
        }
    };

    // Build Payroll button - mirrors the main screen's own guard ("Please enter a pay period
    // number before generating payroll.") via the same info dialog every other "you're missing
    // something" case on this page now uses, then opens a confirm dialog rather than calling
    // usp_Build_Pay_History directly - Confirm there is what actually triggers the API call.
    const handleBuildPayrollClick = () => {
        if (periodNo === undefined) {
            showDialog(DialogList.EMPLOYEE_PAYROLL_NO_PERIOD);
            return;
        }
        setBuildPayrollMessage('');
        showDialog(DialogList.EMPLOYEE_PAYROLL_BUILD_CONFIRM);
    };

    // The Generate dialog's own two buttons - mirrors Build_Pay_History( period_no, null,
    // buildCommissions ) (tsdNo null rebuilds every employee in the period), guarded by
    // Check_Staffing/Duplicate_MultiBilling. A blocked guard comes back as a 400 with the same
    // alert text Oracle would show - surfaced in the dialog (which stays open) rather than
    // silently no-op'ing the way the Form itself does.
    const handleConfirmBuildPayroll = async (buildCommissions: boolean) => {
        if (periodNo === undefined) {
            return;
        }

        setBuildPayrollMessage('');
        setBuildingPayroll(true);
        await post('/v1.0/Payroll/payhistory/build', { periodNo, buildCommissions });
        if (response.ok) {
            hideDialog();
            await fetchPayHistoryItems(lastSearchCriteria ?? { periodNo });
        } else {
            const body = await response.text();
            setBuildPayrollMessage(body || 'Unable to build payroll. Please try again.');
        }
        setBuildingPayroll(false);
    };

    // Text Export File button - mirrors the main screen's own guard (period required first).
    const handleTextExportClick = () => {
        if (periodNo === undefined) {
            showDialog(DialogList.EMPLOYEE_PAYROLL_NO_PERIOD);
            return;
        }
        setTextExportMessage('');
        showDialog(DialogList.EMPLOYEE_PAYROLL_TEXT_EXPORT);
    };

    // The Text dialog's own OK button - mirrors Is_Hours_Entered_Validated + Text_Export_PayCor +
    // Download_AS_File, downloaded through the browser like every other report/export on this
    // page rather than to a path on the user's machine.
    const handleConfirmTextExport = async (filename: string, fileType: number) => {
        if (periodNo === undefined) {
            return;
        }

        setTextExportMessage('');
        setExportingText(true);
        await get(`/v1.0/Payroll/payhistory/textexport?periodNo=${periodNo}&fileType=${fileType}`);
        if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(blobUrl);
            hideDialog();
        } else {
            const body = await response.text();
            setTextExportMessage(body || 'Unable to build the export file. Please try again.');
        }
        setExportingText(false);
    };

    // Excel Report button - mirrors the main screen's own guard (period required first) then
    // Go_Block('Excel'): the real Oracle screen is just a Filename field and OK/Cancel (its
    // file-save-dialog browse button isn't reproduced - the browser's own download flow already
    // covers that role, same as TextExportDialog does for the analogous Text Export screen).
    const handleExcelReportClick = () => {
        if (periodNo === undefined) {
            showDialog(DialogList.EMPLOYEE_PAYROLL_NO_PERIOD);
            return;
        }
        setExcelReportMessage('');
        showDialog(DialogList.EMPLOYEE_PAYROLL_EXCEL_REPORT);
    };

    // The Excel dialog's own OK button - mirrors oid 294's WHEN-BUTTON-PRESSED (the live trigger;
    // oid 292/293 are dead, prefixed OLD_): builds the file against one of two already-deployed
    // reports (/LMSBILLING/Payroll for periods 202611 onward, /LMSBILLING/PayrollV1 for anything
    // earlier - the report-name split happens server-side in GetPayrollExcelReportFile) and
    // downloads it through the browser, same as every other report/export on this page.
    const handleConfirmExcelReport = async (filename: string) => {
        if (periodNo === undefined) {
            return;
        }

        setExcelReportMessage('');
        setGeneratingExcelReport(true);
        await get(`/v1.0/reports/GetReport/PayrollExcelReport?periodNo=${periodNo}`);
        if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(blobUrl);
            hideDialog();
        } else {
            setExcelReportMessage('Unable to generate the Excel report. Please try again.');
        }
        setGeneratingExcelReport(false);
    };

    // Emp. Checklist button - mirrors the main screen's own guard ("Please enter a pay period
    // before accessing the employee checklist...") then Call_Form('emp_te_checklist.fmx', ...,
    // Add_Parameter(pl_id, 'PERIOD_NO', ...)). emp_te_checklist.fmt's own TE_CHECKLIST_OFFICE/
    // TE_CHECKLIST_STATS blocks turned out to already be ported as the Time & Expense Checklist
    // page (Project Billing) - same underlying tables, just previously hardcoded to lm_type='C'
    // (Contractor). Reused here instead of building a new screen, passing lm_type='E' (Employee)
    // via localStorage the same way that page already reads its period/landman context.
    const handleEmpChecklistClick = () => {
        if (periodNo === undefined) {
            showDialog(DialogList.EMPLOYEE_PAYROLL_NO_PERIOD);
            return;
        }
        localStorage.setItem('backOfficeSelectedPayPeriod', String(periodNo));
        localStorage.setItem('backOfficeSelectedLmType', 'E');
        dispatch(push('/project-billing/timeexpensechecklist'));
    };

    // CPI Special button - mirrors Query_Block('Pay_History_CPI_Special'). No period_no guard in
    // Oracle either (this table has no PERIOD_NO column at all).
    const fetchCpiSpecialItems = async () => {
        const result = await get('/v1.0/Payroll/payhistory/cpispecial');
        setCpiSpecialItems(result?.items ?? []);
    };

    const handleCpiSpecialClick = async () => {
        await fetchCpiSpecialItems();
        showDialog(DialogList.EMPLOYEE_PAYROLL_CPI_SPECIAL);
    };

    // The CPI Special dialog's own Save Changes button - mirrors B_OK's commit_data. Unlike Build
    // Payroll/Text Export, this dialog stays open after a successful save (it's a small reference
    // grid the user may keep editing) - re-fetching resets the grid to a clean baseline so the
    // left button toggles back to Close instead of continuing to show Save Changes.
    const handleSaveCpiSpecial = async (items: Array<PayHistoryCpiSpecialItem>) => {
        setSavingCpiSpecial(true);
        await put('/v1.0/Payroll/payhistory/cpispecial', items);
        if (response.ok) {
            await fetchCpiSpecialItems();
        } else {
            alert('Unable to save CPI Special changes. Please try again.');
        }
        setSavingCpiSpecial(false);
    };

    // Lock Period button - mirrors Query_Block('Time_Lock'). No period_no guard in Oracle either.
    const handleLockPeriodClick = async () => {
        const result = await get('/v1.0/Payroll/payhistory/timelock');
        setLockPeriodItems(result?.items ?? []);
        showDialog(DialogList.EMPLOYEE_PAYROLL_LOCK_PERIOD);
    };

    // Set Cutoff EMail Date button - mirrors Query_Block('Time_Emp_Cutoff'). No period_no guard in
    // Oracle either.
    const handleTimeEmpCutoffClick = async () => {
        const result = await get('/v1.0/Payroll/payhistory/timeempcutoff');
        setTimeEmpCutoffItems(result?.items ?? []);
        showDialog(DialogList.EMPLOYEE_PAYROLL_TIME_EMP_CUTOFF);
    };

    // The dialog's own Save Changes button - mirrors the B_OK button's commit_data. Stays open
    // after a successful save, same as CPI Special.
    const handleSaveTimeEmpCutoff = async (items: Array<TimeEmpCutoffItem>) => {
        setSavingTimeEmpCutoff(true);
        await put('/v1.0/Payroll/payhistory/timeempcutoff', items);
        if (response.ok) {
            const result = await get('/v1.0/Payroll/payhistory/timeempcutoff');
            setTimeEmpCutoffItems(result?.items ?? []);
        } else {
            alert('Unable to save cutoff date changes. Please try again.');
        }
        setSavingTimeEmpCutoff(false);
    };

    // Period Count button - mirrors Query_Block('te_checklist_billable_report') - "A pay period
    // must be entered first.." guard, same info dialog every other missing-period case uses.
    const handlePeriodCountClick = async () => {
        if (periodNo === undefined) {
            showDialog(DialogList.EMPLOYEE_PAYROLL_NO_PERIOD);
            return;
        }
        const result = await get(`/v1.0/Payroll/payhistory/periodcount?periodNo=${periodNo}`);
        setPeriodCountItems(result?.items ?? []);
        showDialog(DialogList.EMPLOYEE_PAYROLL_PERIOD_COUNT);
    };

    // Hist. Perf. Bonus button - mirrors B_HIST_PB's WHEN-BUTTON-PRESSED (oid 66), which defaults
    // Expense_Params to a single computed commission period; the dialog itself defaults both
    // Beginning/Ending Period to the currently selected period but leaves them independently
    // editable, since the report itself always supported a real period range.
    const handleHistPerfBonusClick = () => {
        setHistPerfBonusMessage('');
        showDialog(DialogList.EMPLOYEE_PAYROLL_HIST_PERF_BONUS);
    };

    // Mirrors Range_Parms/Expense_Params's OK button -> PAY_COMMRPT_V3.RDF (Report By = Standard) /
    // PAY_COMMPROS_V3.RDF (Report By = Prospect), ported as PerformanceBonusReport.rdl /
    // ProspectPerformanceBonusReport.rdl respectively. Same 204-means-nothing-matched handling as
    // Build Payroll/Text Export's own report-style calls.
    const handleGenerateHistPerfBonusReport = async (params: IHistPerfBonusReportParams) => {
        setHistPerfBonusMessage('');

        if (!params.beginningPeriodNo) {
            setHistPerfBonusMessage('Please enter a beginning pay period.');
            return;
        }

        const query = new URLSearchParams({ begPeriod: params.beginningPeriodNo, includeDayRate: String(!!params.includeDayRate) });
        if (params.endingPeriodNo) {
            query.set('endPeriod', params.endingPeriodNo);
        }
        if (params.mgrNo) {
            query.set('mgrNo', params.mgrNo);
        }
        if (params.clNo) {
            query.set('clNo', params.clNo);
        }

        const reportName = params.reportBy === 'P' ? 'ProspectPerformanceBonusReport' : 'PerformanceBonusReport';
        await get(`/v1.0/reports/GetReport/${reportName}?${query.toString()}`);

        if (!response.ok) {
            setHistPerfBonusMessage('Unable to generate the report. Please try again.');
            return;
        }

        if (response.status === 204) {
            setHistPerfBonusMessage('No commission records matched those filters.');
            return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${reportName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        hideDialog();
    };

    const handleNotYetImplemented = () => alert(NOT_YET_IMPLEMENTED_MESSAGE);
    const buttonWidth = { whiteSpace: 'nowrap' as const, height: '3rem', marginLeft: '.5rem' };

    return (
        <div>
            <PersonnelNavBar />
            <EmployeePayrollSearch runQuery={handleSearch} onPeriodSelected={handlePeriodSelected} />
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel>
                            <div className="form-row" style={{ marginBottom: '.5rem', alignItems: 'center', justifyContent: 'center' }}>
                                <Button
                                    className="blue-hover-button"
                                    style={buttonWidth}
                                    text={buildingPayroll ? 'Building...' : 'Build Payroll'}
                                    disabled={buildingPayroll}
                                    onClick={handleBuildPayrollClick}
                                />
                                <Button className="blue-hover-button" style={buttonWidth} text="Excel Report" onClick={handleExcelReportClick} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Text Export File" onClick={handleTextExportClick} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Save Changes" disabled={!isDirty} onClick={handleSaveChanges} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Emp. Checklist" onClick={handleEmpChecklistClick} />
                                <Button className="blue-hover-button" style={buttonWidth} text="CPI Special" onClick={handleCpiSpecialClick} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Lock Period" onClick={handleLockPeriodClick} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Set Cutoff EMail Date" onClick={handleTimeEmpCutoffClick} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Hist. Perf. Bonus" onClick={handleHistPerfBonusClick} />
                                <Button className="blue-hover-button" style={buttonWidth} text="AM/PM Excel" onClick={handleNotYetImplemented} />
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle="Pay History">
                            <div style={{ overflowX: 'auto' }}>
                                <div style={{ overflowY: 'scroll', maxHeight: '32rem', minWidth: '1500px' }}>
                                    <table style={{ width: '100%' }} key={periodNo}>
                                        <thead>
                                            <tr>
                                                <th>TSDI No.</th>
                                                <th>Employee Name</th>
                                                <th>Dept / Emp No.</th>
                                                <th>Reg Hours</th>
                                                <th>OT Hours</th>
                                                <th>Salary / Rate</th>
                                                <th>Pers. Hours</th>
                                                <th>Vac. Hours</th>
                                                <th>Hol. Hours</th>
                                                <th>Total Hours</th>
                                                <th>Hours Entered</th>
                                                <th>PC Hours</th>
                                                <th>Production Comp.</th>
                                                <th>Performance Bonus</th>
                                                <th>Area Manager Comm.</th>
                                                <th>External Performance Bonus</th>
                                                <th>External Area Manager Comm.</th>
                                                <th>Gross Pay</th>
                                                <th>Expenses</th>
                                                <th>Pay Type</th>
                                                <th>Lock?</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading && (
                                                <tr>
                                                    <td colSpan={21} className="bold-centered">
                                                        <LoadingSpinner />
                                                    </td>
                                                </tr>
                                            )}
                                            {!loading && payHistoryItems.map((item, index) => {
                                                const locked = item.lockRecord === 'Y';
                                                // PC Hours only actually saves anywhere for a "PC" pay type (Build_Pay_History_Name
                                                // computes it as OT_HOURS when PAY_TYPE LIKE '%PC%', else always 0 - see
                                                // SavePayHistoryItem's matching CASE) - disabled otherwise so it doesn't look
                                                // editable when typing into it would silently be discarded on save.
                                                const pcHoursApplicable = !!item.payType?.includes('PC');
                                                return (
                                                    <tr key={`${item.tsdNo}-${index}`} style={{ textAlign: 'center' }}>
                                                        <td>{item.tsdNo}</td>
                                                        <td style={{ textAlign: 'left' }}>{item.name}</td>
                                                        <td>{item.payxDept}{item.payxDept && item.payxNo ? ' / ' : ''}{item.payxNo}</td>
                                                        <td>
                                                            <RHFInputBox
                                                                name={`payHistoryItems[${index}].hours`}
                                                                type="number"
                                                                register={register}
                                                                errors={errors}
                                                                defaultVal={item.hours?.toString()}
                                                                disabled={locked}
                                                                onBlur={() => handleHoursBlur(index)}
                                                                style={{ width: '5rem' }}
                                                                bodyStyle={{ width: '5rem', margin: '0 auto' }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <RHFInputBox
                                                                name={`payHistoryItems[${index}].otHours`}
                                                                type="number"
                                                                register={register}
                                                                errors={errors}
                                                                defaultVal={item.otHours?.toString()}
                                                                disabled={locked}
                                                                onBlur={() => handleHoursBlur(index)}
                                                                style={{ width: '5rem' }}
                                                                bodyStyle={{ width: '5rem', margin: '0 auto' }}
                                                            />
                                                        </td>
                                                        <td>{formatCurrency(item.wSalary)}</td>
                                                        <td>
                                                            <RHFInputBox
                                                                name={`payHistoryItems[${index}].persHours`}
                                                                type="number"
                                                                register={register}
                                                                errors={errors}
                                                                defaultVal={item.persHours?.toString()}
                                                                disabled={locked}
                                                                onBlur={() => handleHoursBlur(index)}
                                                                style={{ width: '5rem' }}
                                                                bodyStyle={{ width: '5rem', margin: '0 auto' }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <RHFInputBox
                                                                name={`payHistoryItems[${index}].vacHours`}
                                                                type="number"
                                                                register={register}
                                                                errors={errors}
                                                                defaultVal={item.vacHours?.toString()}
                                                                disabled={locked}
                                                                onBlur={() => handleHoursBlur(index)}
                                                                style={{ width: '5rem' }}
                                                                bodyStyle={{ width: '5rem', margin: '0 auto' }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <RHFInputBox
                                                                name={`payHistoryItems[${index}].holHours`}
                                                                type="number"
                                                                register={register}
                                                                errors={errors}
                                                                defaultVal={item.holHours?.toString()}
                                                                disabled={locked}
                                                                onBlur={() => handleHoursBlur(index)}
                                                                style={{ width: '5rem' }}
                                                                bodyStyle={{ width: '5rem', margin: '0 auto' }}
                                                            />
                                                        </td>
                                                        <td>{item.totHours}</td>
                                                        <td>{item.teHours}</td>
                                                        <td>
                                                            <RHFInputBox
                                                                name={`payHistoryItems[${index}].pcHours`}
                                                                type="number"
                                                                register={register}
                                                                errors={errors}
                                                                defaultVal={item.pcHours?.toString()}
                                                                disabled={locked || !pcHoursApplicable}
                                                                title={pcHoursApplicable ? undefined : 'Only applies to PC pay types - has no effect for this employee\'s pay type.'}
                                                                style={{ width: '5rem' }}
                                                                bodyStyle={{ width: '5rem', margin: '0 auto' }}
                                                            />
                                                        </td>
                                                        <td>{formatCurrency(item.prodBonus)}</td>
                                                        <td>{formatCurrency(item.commission)}</td>
                                                        <td>{formatCurrency(item.areaCommission)}</td>
                                                        <td>{formatCurrency(item.extCommission)}</td>
                                                        <td>{formatCurrency(item.extAreaCommission)}</td>
                                                        <td>
                                                            <RHFInputBox
                                                                name={`payHistoryItems[${index}].grossPay`}
                                                                type="number"
                                                                register={register}
                                                                errors={errors}
                                                                defaultVal={item.grossPay?.toString()}
                                                                disabled={locked}
                                                                style={{ width: '6rem' }}
                                                                bodyStyle={{ width: '6rem', margin: '0 auto' }}
                                                            />
                                                        </td>
                                                        <td>{formatCurrency(item.expenses)}</td>
                                                        <td>{item.payType}</td>
                                                        <td>
                                                            <RHFCheckbox
                                                                name={`payHistoryItems[${index}].lockRecord`}
                                                                register={register}
                                                                defaultChecked={locked}
                                                                bodyStyle={{ width: 'auto', margin: '0 auto', display: 'inline-block' }}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {!loading && payHistoryItems.length === 0 && (
                                                <tr>
                                                    <td colSpan={21} className="bold-centered">No Records Found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="form-row" style={{ marginTop: '.5rem', alignItems: 'center', justifyContent: 'center' }}>
                                <Button className="blue-hover-button" style={buttonWidth} text="Period Count" onClick={handlePeriodCountClick} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Lock All" onClick={() => handleLockAll(true)} />
                                <Button className="blue-hover-button" style={buttonWidth} text="Unlock All" onClick={() => handleLockAll(false)} />
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <NoSelectedInvoicesWarningDialog
                dialogName={DialogList.EMPLOYEE_PAYROLL_NO_PERIOD}
                hideDialog={hideDialog}
                heading="No Pay Period Entered"
                label="Please enter a pay period number first."
            />
            <BuildPayrollConfirmDialog
                dialogName={DialogList.EMPLOYEE_PAYROLL_BUILD_CONFIRM}
                hideDialog={hideDialog}
                building={buildingPayroll}
                message={buildPayrollMessage}
                onConfirm={handleConfirmBuildPayroll}
            />
            <TextExportDialog
                dialogName={DialogList.EMPLOYEE_PAYROLL_TEXT_EXPORT}
                hideDialog={hideDialog}
                periodNo={periodNo}
                exporting={exportingText}
                message={textExportMessage}
                onConfirm={handleConfirmTextExport}
            />
            <ExcelReportDialog
                dialogName={DialogList.EMPLOYEE_PAYROLL_EXCEL_REPORT}
                hideDialog={hideDialog}
                periodNo={periodNo}
                generating={generatingExcelReport}
                message={excelReportMessage}
                onConfirm={handleConfirmExcelReport}
            />
            <CpiSpecialDialog
                dialogName={DialogList.EMPLOYEE_PAYROLL_CPI_SPECIAL}
                hideDialog={hideDialog}
                items={cpiSpecialItems}
                saving={savingCpiSpecial}
                onSave={handleSaveCpiSpecial}
            />
            <LockPeriodDialog
                dialogName={DialogList.EMPLOYEE_PAYROLL_LOCK_PERIOD}
                hideDialog={hideDialog}
                items={lockPeriodItems}
            />
            <TimeEmpCutoffDialog
                dialogName={DialogList.EMPLOYEE_PAYROLL_TIME_EMP_CUTOFF}
                hideDialog={hideDialog}
                items={timeEmpCutoffItems}
                saving={savingTimeEmpCutoff}
                onSave={handleSaveTimeEmpCutoff}
            />
            <PeriodCountDialog
                dialogName={DialogList.EMPLOYEE_PAYROLL_PERIOD_COUNT}
                hideDialog={hideDialog}
                items={periodCountItems}
            />
            <HistPerfBonusDialog
                dialogName={DialogList.EMPLOYEE_PAYROLL_HIST_PERF_BONUS}
                hideDialog={hideDialog}
                onGenerateReport={handleGenerateHistPerfBonusReport}
                defaultPeriodNo={periodNo}
                message={histPerfBonusMessage}
            />
        </div>
    );
};

export default EmployeePayrollPage;



// WEBPACK FOOTER //
// ./src/components/personnel/pages/EmployeePayrollPage.tsx